import { BlockType } from '@next-space/fe-api-idl';
import isHotkey from 'is-hotkey';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTransaction } from 'src/hooks/use-transaction';
import { updateBlock } from 'src/redux/managers/block/update';
import { patchExpand } from 'src/redux/managers/ui/use-fold';
import { cache } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { $appUiStateCache } from 'src/services/app';
import { getAllEditorInfoFromDomContent } from 'src/utils/block-utils';
import { focusEditable } from 'src/utils/editor';
import { getEditorKey } from 'src/utils/editor-utils';

export const useModEnterKey = () => {
  const transaction = useTransaction();
  const history = useHistory();
  const modShiftEnterKey = (event: KeyboardEvent) => {
    if (isHotkey('mod+enter')(event)) {
      const node = document.querySelector('.file-preview');
      if (node) {
        // 关闭预览窗口
        const modalContent = document.getElementById('modalContent');
        if (modalContent instanceof HTMLElement) {
          modalContent.click();
        }
        return;
      }
      if (isFileBlock()) {
        const uuid = cache.ui.selectedBlocks[0]?.blockId;
        if (!uuid) return;
        // 打开预览窗口
        const node = document.querySelector(`[data-block-id="${uuid}"] .preview-file`);
        if (node instanceof HTMLElement) {
          node.click();
        }
        return;
      }
      // 如果首个是todo或者折叠块，则只对首个类型的块进行操作（抄notion的交互，自己试下notion多选就知道了）
      let blockType: BlockType | undefined = undefined;
      let firstBlockCheckedOrExpanded = false;

      const update = (block: NextBlock) => {
        if (block.type === BlockType.TODO) {
          updateBlock(block.uuid, {
            data: {
              checked: !firstBlockCheckedOrExpanded,
            },
          });
        } else if ([BlockType.FOLD_LIST, BlockType.TOGGLE_HEADER].includes(block.type)) {
          patchExpand(block.uuid, !firstBlockCheckedOrExpanded);
        } else if (block.type === BlockType.CODE) {
          const editableList = getAllEditorInfoFromDomContent(false);
          const editableIndex = editableList.findIndex((v) => v.blockId === block.uuid);
          const index = editableIndex + 1;
          if (index < editableList.length) {
            const nextEditable = editableList[index];
            if (!nextEditable) return;
            event.preventDefault();
            const editorKey = getEditorKey(block.uuid);
            if (!editorKey) return;
            focusEditable(nextEditable.editorKey, Infinity, Infinity);
          }
        }
      };
      const updateFirstBlockCheckedOrExpanded = (block: NextBlock) => {
        if ([BlockType.TODO, BlockType.FOLD_LIST, BlockType.TOGGLE_HEADER].includes(block.type)) {
          blockType = block.type;
          switch (block.type) {
            case BlockType.TODO: {
              firstBlockCheckedOrExpanded = !!block.data.checked;
              break;
            }
            case BlockType.TOGGLE_HEADER:
            case BlockType.FOLD_LIST: {
              firstBlockCheckedOrExpanded = !!$appUiStateCache.$expandFoldRecord[block.uuid];
              break;
            }
            default:
          }
        }
      };
      transaction(() => {
        if (cache.ui.selectedBlocks.length > 0) {
          const firstBlock = cache.blocks[cache.ui.selectedBlocks[0]?.blockId ?? ''];
          if (firstBlock?.type === BlockType.PAGE) {
            history.push(`/${firstBlock.uuid}`);
            return;
          }
          cache.ui.selectedBlocks.forEach((selectedBlock) => {
            const block = cache.blocks[selectedBlock.blockId];
            if (!block) return;
            if (blockType === undefined) {
              updateFirstBlockCheckedOrExpanded(block);
            }
            if (block.type === blockType) {
              update(block);
            }
          });
        } else {
          const blockId = getFocusedBlockId();
          if (!blockId) return;
          const block = cache.blocks[blockId];
          if (!block) return;
          updateFirstBlockCheckedOrExpanded(block);
          update(block);
        }
      });
    }
  };

  return useCallback(modShiftEnterKey, [history, transaction]);
};

const getFocusedBlockId = () => {
  const uuid = document.activeElement?.closest('[data-block-id]')?.getAttribute('data-block-id');
  return uuid;
};
function isFileBlock() {
  if (cache.ui.selectedBlocks.length !== 1) return false;
  const uuid = cache.ui.selectedBlocks[0]?.blockId;
  if (!uuid) return false;
  const block = cache.blocks[uuid];
  if (!block) return false;
  if (block?.type === BlockType.FILE && block.data.ossName) return true;
  if (block.type === BlockType.EXTERNAL_FILE && block.data.link) return true;
}
